/**
 * Affiliate (influencer) service — stateless orchestration for the control
 * panel and the affiliate portal. Screens call this and never touch
 * `affiliateApi` or `apiClient`.
 *
 * `startIndex` on this controller is a 1-based *page* number, not a row offset:
 * page 2 is `startIndex: 2`, not `20`. The name is misleading and the meaning is
 * not consistent across the API — `GetReportedPosts` and `LeaderBoardList` do
 * take a row offset under the same field name — so it is converted here, once,
 * and every screen keeps using plain page numbers.
 *
 * Referral-code and profile reads accept an optional `auth`: the portal passes
 * the affiliate's own bearer, the control panel omits it and runs on the
 * operator's member session.
 */
import { UnusableResponseError } from '../../utils/apiErrorHandler';
import { isPagedEnvelope } from '../admin/adminMapper';
import { createInvalidOtpError } from '../auth/authErrors';
import affiliateApi, { type AffiliateAuth } from './affiliateApi';
import {
  buildAffiliateEarningsSummary,
  toAffiliateEarningsRangePayload,
} from './affiliateEarnings';
import AFFILIATE_ENDPOINTS from './affiliateEndpoints';
import {
  createAffiliateNotApprovedError,
  createAffiliateRequestError,
} from './affiliateErrors';
import {
  affiliateQualificationStatus,
  affiliateQualificationStatusId,
  affiliateReferralCriteria,
  type AffiliateReferralOverrideDecision,
} from './affiliateQualification';
import {
  AFFILIATE_REVIEW_DECISION_STATUS_IDS,
  affiliateIsDeleted,
  affiliateReviewState,
  affiliateStatusIdFromState,
  type AffiliateReviewDecision,
} from './affiliateStatus';
import type {
  Affiliate,
  AffiliateEarningsFilter,
  AffiliateEarningsSummary,
  AffiliateMutationResponse,
  AffiliatePasswordReset,
  AffiliatePayout,
  AffiliateProfile,
  AffiliateQualificationRule,
  AffiliateQualificationRuleResponse,
  AffiliateQualificationStatus,
  AffiliateReferral,
  AffiliateReferralCode,
  AffiliateReferralCodeResponse,
  AffiliateReferralOverride,
  AffiliateReferralPage,
  AffiliateReferralResponse,
  AffiliateSession,
  AffiliateStatusFilter,
  AffiliateUserDetailResponse,
  AffiliateUserResponse,
} from './affiliateTypes';

const DEFAULT_PAGE_SIZE = 20;

/**
 * `GetAffiliateReferralCodes` pages now. Neither screen that reads it paginates
 * — both show every code an affiliate owns beside the totals — so one page wide
 * enough for that is requested and `totalCount` is returned so a caller can tell
 * when even this was not enough.
 */
const REFERRAL_CODE_PAGE_SIZE = 100;

const text = (value: string | null | undefined): string => value ?? '';

/** The earnings roll-ups are newer than some deployments; treat a gap as zero. */
const amount = (value: number | null | undefined): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : 0;

/**
 * The signup breakdown, with `usageCount` as the fallback for `totalSignups`.
 *
 * The API keeps `usageCount` as an alias of `totalSignups` for backward
 * compatibility, so a deployment that has not rolled the new field out yet
 * still fills the Signups figure instead of reporting 0 beside a populated
 * earnings total.
 */
const mapQualificationRollUp = (
  item: Partial<{
    usageCount: number;
    totalSignups: number;
    eligibleCount: number;
    pendingCount: number;
    rejectedCount: number;
    reversedCount: number;
    pendingEarnings: number;
  }>,
) => ({
  totalSignups:
    typeof item.totalSignups === 'number' && Number.isFinite(item.totalSignups)
      ? item.totalSignups
      : amount(item.usageCount),
  eligibleCount: amount(item.eligibleCount),
  pendingCount: amount(item.pendingCount),
  rejectedCount: amount(item.rejectedCount),
  reversedCount: amount(item.reversedCount),
  pendingEarnings: amount(item.pendingEarnings),
});

const mapAffiliate = (item: AffiliateUserResponse): Affiliate => ({
  affiliateUserId: item.affiliateUserId,
  email: text(item.email),
  address: [text(item.address1), text(item.address2)].filter(Boolean).join(', '),
  country: text(item.country),
  howDidYouHear: text(item.howDidYouHear),
  state: affiliateReviewState(item),
  statusName: text(item.statusName),
  reviewComments: text(item.reviewComments),
  isDeleted: affiliateIsDeleted(item),
  createdDate: text(item.createdDate),
  reviewedDate: text(item.reviewedDate),
  usageCount: amount(item.usageCount),
  totalEarnings: amount(item.totalEarnings),
  ...mapQualificationRollUp(item),
});

const mapReferralCode = (
  item: AffiliateReferralCodeResponse,
): AffiliateReferralCode => ({
  id: item.affiliateReferralCodeId,
  affiliateUserId: item.affiliateUserId,
  code: text(item.referralCode),
  description: text(item.description),
  commissionPerDownload: item.commissionPerDownload ?? 0,
  isActive: item.isActive === true,
  isDeleted: item.isDeleted === true,
  createdDate: text(item.createdDate),
  usageCount: amount(item.usageCount),
  totalEarnings: amount(item.totalEarnings),
  ...mapQualificationRollUp(item),
});

/**
 * Whether the API withheld the referred user's identity.
 *
 * Masking is applied per bearer: an affiliate reading their own referrals gets
 * `null` for `email`, `profilePicture`, the registration IP and device, and
 * `reviewedBy`, while `userName` arrives as `K***`. The flag is read from the
 * fields the API nulls rather than from who is asking, so the row renders for
 * whichever shape actually arrived — a masked row must never be laid out as if
 * an avatar and a full name were coming.
 */
const isReferralIdentityMasked = (item: AffiliateReferralResponse): boolean =>
  item.email == null || item.profilePicture == null;

const mapReferral = (item: AffiliateReferralResponse): AffiliateReferral => ({
  id: item.affiliateReferralId,
  affiliateReferralCodeId: item.affiliateReferralCodeId,
  affiliateUserId: item.affiliateUserId,
  referralCodeUsed: text(item.referralCodeUsed),
  referralCode: text(item.referralCode),
  affiliateEmail: text(item.affiliateEmail),
  referredUserId: item.referredUserId,
  userGuid: text(item.userGUID),
  userName: text(item.userName),
  email: text(item.email),
  firstName: text(item.firstName),
  lastName: text(item.lastName),
  profilePicture: text(item.profilePicture),
  registeredAt: text(item.registeredAt),
  registrationIpAddress: text(item.registrationIpAddress),
  registrationDeviceId: text(item.registrationDeviceId),
  commissionAmount: amount(item.commissionAmount),
  eligibleFromDate: text(item.eligibleFromDate),
  status: affiliateQualificationStatus(item),
  statusLabel: text(item.qualificationStatus),
  // Straight from the API: payability is its rule, not a test for 2 or 5.
  isEligible: item.isEligible === true,
  qualifiedAt: text(item.qualifiedAt),
  rejectionReason: text(item.rejectionReason),
  criteriaMetMask: amount(item.criteriaMetMask),
  criteria: affiliateReferralCriteria(item),
  lastEvaluatedAt: text(item.lastEvaluatedAt),
  appliedRuleId:
    typeof item.appliedRuleId === 'number' ? item.appliedRuleId : null,
  isGrandfathered: item.isGrandfathered === true,
  reviewedBy: typeof item.reviewedBy === 'number' ? item.reviewedBy : null,
  reviewedDate: text(item.reviewedDate),
  reviewComments: text(item.reviewComments),
  activity: {
    posts: amount(item.postCount),
    comments: amount(item.commentCount),
    reactions: amount(item.reactionCount),
    shares: amount(item.shareCount),
    commentReactions: amount(item.commentReactionCount),
    groupJoins: amount(item.groupJoinCount),
  },
  isEmailVerified: item.isEmailVerified === true,
  isUserBlocked: item.isUserBlocked === true,
  userIsActive: item.userIsActive === true,
  userIsDeleted: item.userIsDeleted === true,
  isIdentityMasked: isReferralIdentityMasked(item),
});

const mapQualificationRule = (
  item: AffiliateQualificationRuleResponse,
): AffiliateQualificationRule => ({
  id: item.affiliateQualificationRuleId,
  ruleName: text(item.ruleName),
  ruleDescription: text(item.ruleDescription),
  minTenureDays: amount(item.minTenureDays),
  minActiveLoginDays: amount(item.minActiveLoginDays),
  minActivityCount: amount(item.minActivityCount),
  minActivityDistinctDays: amount(item.minActivityDistinctDays),
  requireEmailVerified: item.requireEmailVerified === true,
  countPosts: item.countPosts === true,
  countComments: item.countComments === true,
  countPostReactions: item.countPostReactions === true,
  countPostShares: item.countPostShares === true,
  countCommentReactions: item.countCommentReactions === true,
  countGroupJoins: item.countGroupJoins === true,
  isActive: item.isActive === true,
  effectiveFrom: text(item.effectiveFrom),
  modifiedDate: text(item.modifiedDate),
});

const mapProfile = (item: AffiliateUserDetailResponse): AffiliateProfile => ({
  affiliateUserId: item.affiliateUserId,
  email: text(item.email),
  password: text(item.password),
  address1: text(item.address1),
  address2: text(item.address2),
  country: text(item.country),
  howDidYouHear: text(item.howDidYouHear),
  state: affiliateReviewState(item),
});

/**
 * The affiliate auth endpoints answer 200 with a failing `status`/`statusCode`
 * instead of an HTTP error, so `apiClient` treats a refusal as a success. Both
 * shapes are normalised here rather than in a screen.
 */
const rejectedByStatus = (payload: {
  status?: string | null;
  statusCode?: number;
}): boolean => {
  if (typeof payload.statusCode === 'number' && payload.statusCode >= 400) {
    return true;
  }
  return typeof payload.status === 'string'
    ? payload.status.toLowerCase() !== 'success'
    : false;
};

/**
 * Normalizes the referral-code mutation envelope. These endpoints can return
 * the contradictory combination `success: false` with a successful embedded
 * status code and success message after persisting the mutation. The embedded
 * status code is authoritative; `success` remains the fallback when the code
 * is absent.
 */
const assertMutationAccepted = (
  data: AffiliateMutationResponse | undefined,
  endpoint: string,
): void => {
  if (!data) {
    throw new UnusableResponseError(endpoint);
  }
  // The status *code* only: the documented envelope has no `status` string, so
  // an undocumented one ("Created") must not be read as a refusal.
  const successfulStatus =
    typeof data.statusCode === 'number' &&
    data.statusCode >= 200 &&
    data.statusCode < 300;
  if (
    data.statusCode >= 400 ||
    (!successfulStatus && data.success === false)
  ) {
    throw createAffiliateRequestError(data.message);
  }
};

export interface AffiliateListParams {
  searchKeyword?: string;
  /** Whether soft-deleted affiliates should be included in the result. */
  includeDeleted?: boolean;
  /**
   * One status to narrow to, or `null` for every application. Sent through the
   * API's supported `statusIdFilter` field.
   */
  state?: AffiliateStatusFilter | null;
  pageNumber?: number;
  pageSize?: number;
  /** Only the interval is sent: this endpoint has no payment-state filter. */
  earningsFilter?: AffiliateEarningsFilter;
}

export interface AffiliatePage {
  items: Affiliate[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  /** Roll-up across the rows on this page, not across the whole result set. */
  earnings: AffiliateEarningsSummary;
}

/**
 * `GetAffiliateReferralCodes` reports the payment roll-up beside the rows, so
 * this is the only read that can answer what an affiliate has been paid and is
 * still owed.
 */
export interface AffiliateReferralCodeList {
  codes: AffiliateReferralCode[];
  earnings: AffiliateEarningsSummary;
  /** Codes the API holds, which is more than `codes` once one page is not enough. */
  totalCount: number;
}

export interface AffiliateReferralCodeParams {
  auth?: AffiliateAuth;
  earningsFilter?: AffiliateEarningsFilter;
  /**
   * Narrow to active or deactivated codes. Left unset the API decides, which
   * is what every screen that shows a status column wants: the rows arrive with
   * their own `isActive` and are labelled from it.
   */
  isActiveFilter?: number | null;
}

export interface AffiliateReferralParams {
  /** The affiliate's own bearer; omitted for an operator read. */
  auth?: AffiliateAuth;
  /**
   * Scope to one affiliate. Ignored for a non-admin bearer — the API pins the
   * request to the caller — and omitted by an operator who wants every
   * affiliate's referrals.
   */
  affiliateUserId?: number | null;
  /** Scope to one referral code. */
  affiliateReferralCodeId?: number | null;
  /** One qualification status to narrow to, or `null` for every one. */
  status?: AffiliateQualificationStatus | null;
  searchKeyword?: string;
  /** Only the interval is used; this endpoint has no payment-state filter. */
  earningsFilter?: AffiliateEarningsFilter;
  pageNumber?: number;
  pageSize?: number;
}

const affiliateService = {
  getAffiliates: async (
    params: AffiliateListParams = {},
  ): Promise<AffiliatePage> => {
    const pageNumber = params.pageNumber ?? 1;
    const pageSize = params.pageSize ?? DEFAULT_PAGE_SIZE;
    const range = params.earningsFilter
      ? toAffiliateEarningsRangePayload(params.earningsFilter)
      : { startDate: null, endDate: null };
    const { data } = await affiliateApi.list({
      searchKeyword: params.searchKeyword ? params.searchKeyword : null,
      statusIdFilter: params.state ? affiliateStatusIdFromState(params.state) : null,
      includeDeleted: params.includeDeleted === true,
      startDate: range.startDate,
      endDate: range.endDate,
      // A page number, not `(pageNumber - 1) * pageSize` — see the file header.
      startIndex: pageNumber,
      pageSize,
    });

    // `affiliates` is nullable in the contract, so an empty page is valid.
    if (!isPagedEnvelope(data, 'affiliates')) {
      throw new UnusableResponseError(AFFILIATE_ENDPOINTS.LIST);
    }

    const totalCount = data.totalRecords ?? 0;
    const totalPages = pageSize > 0 ? Math.ceil(totalCount / pageSize) : 0;
    const items = (Array.isArray(data.affiliates) ? data.affiliates : []).map(
      mapAffiliate,
    );
    return {
      items,
      totalCount,
      pageNumber,
      pageSize,
      totalPages,
      hasNextPage: pageNumber < totalPages,
      hasPreviousPage: pageNumber > 1,
      // No paid/owed figures: `AffiliateUserResponse` carries the earnings and
      // signup roll-up only, so the payment breakdown is read per affiliate
      // instead. `pendingEarnings` is on the row, so it is summed here — it is
      // what explains why the eligible-only total dropped.
      earnings: buildAffiliateEarningsSummary(
        {
          totalEarnings: items.reduce((sum, row) => sum + row.totalEarnings, 0),
          usageCount: items.reduce((sum, row) => sum + row.totalSignups, 0),
        },
        {
          pendingEarnings: items.reduce(
            (sum, row) => sum + row.pendingEarnings,
            0,
          ),
        },
        {
          totalSignups: items.reduce((sum, row) => sum + row.totalSignups, 0),
          eligibleCount: items.reduce((sum, row) => sum + row.eligibleCount, 0),
          pendingCount: items.reduce((sum, row) => sum + row.pendingCount, 0),
          rejectedCount: items.reduce((sum, row) => sum + row.rejectedCount, 0),
          reversedCount: items.reduce((sum, row) => sum + row.reversedCount, 0),
        },
      ),
    };
  },

  /**
   * Records an operator's review decision. The API takes the target `statusId`
   * rather than a yes/no, which is what makes suspending an already approved
   * affiliate expressible.
   *
   * AdminReviewAffiliate is the one affiliate operation whose 200 body carries
   * no `success` flag, so `apiClient` cannot reject it. A refusal arrives as
   * 200 with a failing `statusCode`/`status`, which must be turned into an
   * error here or the operator is told a rejected review succeeded.
   */
  reviewAffiliate: async (
    affiliateUserId: number,
    decision: AffiliateReviewDecision,
    actionBy: number,
    reviewComments?: string,
  ): Promise<void> => {
    const { data } = await affiliateApi.review({
      affiliateUserId,
      statusId: AFFILIATE_REVIEW_DECISION_STATUS_IDS[decision],
      reviewComments: reviewComments?.trim() ? reviewComments.trim() : null,
      actionBy,
    });
    const statusCode = data?.statusCode;
    if (typeof statusCode === 'number' && statusCode >= 400) {
      throw new UnusableResponseError(AFFILIATE_ENDPOINTS.ADMIN_REVIEW);
    }
  },

  /**
   * Soft-deletes an affiliate account; the API deactivates that affiliate's
   * referral codes with it, so every code they own stops crediting downloads.
   *
   * Like AdminReviewAffiliate the 200 body carries no `success` flag, so the
   * status *code* is the only guard. Deliberately not `rejectedByStatus`: this
   * envelope's `status` string is undocumented in value, and reading an
   * accepted "Deleted" as a refusal would tell the operator the account is
   * still there and invite them to delete it again.
   */
  deleteAffiliate: async (affiliateUserId: number): Promise<void> => {
    const { data } = await affiliateApi.deleteAffiliate({ affiliateUserId });
    if (!data) {
      throw new UnusableResponseError(AFFILIATE_ENDPOINTS.DELETE);
    }
    if ((data.statusCode ?? 0) >= 400) {
      throw createAffiliateRequestError(data.message);
    }
  },

  /**
   * Signs the affiliate in. A refusal can arrive either as an HTTP error or as
   * 200 with no token, so the body is normalised into a rejection too; a valid
   * password on an account that is not approved is rejected separately so the
   * portal can say why instead of claiming the credentials were wrong.
   *
   * The envelope's own `isActive` is still the legacy integer while
   * `affiliateInfo` carries `statusId`, so the state is read from whichever
   * shape arrived rather than from one named field.
   */
  login: async (email: string, password: string): Promise<AffiliateSession> => {
    const { data } = await affiliateApi.login({ email, password });
    if (!data || rejectedByStatus(data) || !data.token) {
      throw createAffiliateRequestError(data?.message);
    }

    const info = data.affiliateInfo;
    const state = affiliateReviewState(info ?? { isActive: data.isActive });
    if (state !== 'approved') {
      throw createAffiliateNotApprovedError(state);
    }

    const affiliateUserId = info?.affiliateUserId ?? data.affiliateUserId;
    if (!affiliateUserId) {
      throw new UnusableResponseError(AFFILIATE_ENDPOINTS.VALIDATE_LOGIN);
    }

    return {
      token: data.token,
      affiliateUserId,
      email: text(info?.email) || email,
      state,
    };
  },

  /**
   * Starts a password reset and returns the token the OTP step needs.
   *
   * The API answers 200 with a token for an address that has no affiliate
   * account, deliberately, so this cannot report whether the email is
   * registered — an unknown address simply never receives a code.
   */
  forgotPassword: async (email: string): Promise<AffiliatePasswordReset> => {
    const { data } = await affiliateApi.forgotPassword({ email });
    if (!data || rejectedByStatus(data)) {
      throw createAffiliateRequestError(data?.message);
    }
    if (!data.passwordResetToken) {
      throw new UnusableResponseError(AFFILIATE_ENDPOINTS.FORGOT_PASSWORD);
    }
    return { passwordResetToken: data.passwordResetToken };
  },

  /**
   * Verifies the emailed code. A wrong one arrives as 200 `{ isValid: false }`,
   * so it is turned into the same invalid-OTP rejection the member flow raises
   * and the screen can tell it apart from a transport failure.
   */
  verifyPasswordResetOtp: async (payload: {
    passwordResetToken: string;
    otpCode: string;
  }): Promise<void> => {
    const { data } = await affiliateApi.verifyPasswordResetOtp({
      passwordResetToken: payload.passwordResetToken,
      otpCode: payload.otpCode,
    });
    if (data?.isValid === false) {
      throw createInvalidOtpError(data.message ?? undefined);
    }
    // Only an explicit `false` counts as a wrong code; anything else that is
    // not a success is a request failure.
    if (!data || rejectedByStatus(data)) {
      throw createAffiliateRequestError(data?.message);
    }
  },

  /** Replays the verified OTP with the new password; the token alone is not enough. */
  resetPassword: async (payload: {
    email: string;
    passwordResetToken: string;
    otpCode: string;
    newPassword: string;
  }): Promise<void> => {
    const { data } = await affiliateApi.resetPassword({
      email: payload.email,
      passwordResetToken: payload.passwordResetToken,
      otpCode: payload.otpCode,
      newPassword: payload.newPassword,
    });
    if (!data || rejectedByStatus(data)) {
      throw createAffiliateRequestError(data?.message);
    }
  },

  /**
   * Files an affiliate application. Approval is a separate operator step, so a
   * success here does not grant a session — the portal sends the applicant to
   * sign-in and the account stays `pending` until reviewed.
   */
  register: async (payload: {
    email: string;
    password: string;
    address1: string;
    address2?: string;
    countryId: number;
    howDidYouHear: string;
  }): Promise<void> => {
    const { data } = await affiliateApi.register({
      email: payload.email,
      password: payload.password,
      address1: payload.address1,
      address2: payload.address2 ? payload.address2 : null,
      countryId: payload.countryId,
      howDidYouHear: payload.howDidYouHear,
    });
    if (!data || rejectedByStatus(data)) {
      throw createAffiliateRequestError(data?.message);
    }
  },

  getProfile: async (
    affiliateUserId: number,
    auth?: AffiliateAuth,
  ): Promise<AffiliateProfile> => {
    const { data } = await affiliateApi.getById({ affiliateUserId }, auth);
    if (!data || rejectedByStatus(data) || !data.user) {
      throw new UnusableResponseError(AFFILIATE_ENDPOINTS.BY_ID);
    }
    return mapProfile(data.user);
  },

  /**
   * `password` is mandatory on this endpoint, so an edit that does not change it
   * must resend the stored one — the caller passes the value read back from
   * `getProfile`.
   */
  updateProfile: async (
    payload: {
      affiliateUserId: number;
      email: string;
      password: string;
      address1: string;
      address2?: string;
      countryId: number;
      howDidYouHear: string;
      /** Operator editing on the affiliate's behalf; absent on portal calls. */
      actionBy?: number;
    },
    auth?: AffiliateAuth,
  ): Promise<AffiliateProfile> => {
    const { data } = await affiliateApi.updateProfile(
      {
        affiliateUserId: payload.affiliateUserId,
        email: payload.email,
        password: payload.password,
        address1: payload.address1,
        address2: payload.address2 ? payload.address2 : null,
        countryId: payload.countryId,
        howDidYouHear: payload.howDidYouHear,
        actionBy: payload.actionBy ?? null,
      },
      auth,
    );
    if (!data || rejectedByStatus(data)) {
      throw createAffiliateRequestError(data?.message);
    }
    if (!data.user) {
      throw new UnusableResponseError(AFFILIATE_ENDPOINTS.UPDATE_PROFILE);
    }
    return mapProfile(data.user);
  },

  getReferralCodes: async (
    affiliateUserId: number,
    params: AffiliateReferralCodeParams = {},
  ): Promise<AffiliateReferralCodeList> => {
    const range = params.earningsFilter
      ? toAffiliateEarningsRangePayload(params.earningsFilter)
      : { startDate: null, endDate: null };
    const { data } = await affiliateApi.referralCodes(
      {
        affiliateUserId,
        isActiveFilter: params.isActiveFilter ?? null,
        startDate: range.startDate,
        endDate: range.endDate,
        // First page, same page-number convention as the affiliate list.
        startIndex: 1,
        pageSize: REFERRAL_CODE_PAGE_SIZE,
      },
      params.auth,
    );
    // An affiliate with no codes yet is a normal state, not a failure.
    if (!data || (!data.success && !Array.isArray(data.referralCodes))) {
      throw new UnusableResponseError(AFFILIATE_ENDPOINTS.REFERRAL_CODES);
    }
    const codes = (Array.isArray(data.referralCodes) ? data.referralCodes : []).map(
      mapReferralCode,
    );
    return {
      codes,
      // A genuine 0 is kept; only a deployment that omits the field falls back
      // to the row count, so a caller comparing the two can spot truncation.
      totalCount:
        typeof data.totalRecords === 'number' && Number.isFinite(data.totalRecords)
          ? data.totalRecords
          : codes.length,
      earnings: buildAffiliateEarningsSummary(
        {
          // The envelope total covers every code, including any the page omits.
          // Eligible referrals only since the qualification release.
          totalEarnings: amount(data.totalEarnings),
          usageCount:
            typeof data.totalSignups === 'number' &&
            Number.isFinite(data.totalSignups)
              ? data.totalSignups
              : codes.reduce((sum, code) => sum + code.usageCount, 0),
        },
        {
          paidEarnings: data.totalPaid,
          dueEarnings: data.unpaidBalance,
          unclaimedEarnings: data.unclaimed,
          lifetimeEarnings: data.lifetimeEarned,
          lifetimePaidEarnings: data.lifetimePaid,
          pendingEarnings: data.pendingEarnings,
        },
        {
          totalSignups: data.totalSignups,
          eligibleCount: data.eligibleCount,
          pendingCount: data.pendingCount,
          rejectedCount: data.rejectedCount,
          reversedCount: data.reversedCount,
        },
      ),
    };
  },

  /**
   * The referred users behind an affiliate's signup count.
   *
   * One route, two shapes: an operator's bearer answers the full record for
   * whichever affiliate was asked for, while an affiliate's bearer is pinned to
   * their own referrals with the referred user's identity masked. `params.auth`
   * therefore decides what comes back, and a portal call that forgot it would
   * read as the operator — `affiliateApi.withAuth` refuses that by sending no
   * bearer at all rather than falling back to the member session.
   *
   * `affiliateUserId` is only sent when positive: the API treats <= 0 as "every
   * affiliate", and sending a 0 from an unset state would silently widen an
   * operator's scoped drill-down to the global list.
   */
  getReferrals: async (
    params: AffiliateReferralParams = {},
  ): Promise<AffiliateReferralPage> => {
    const pageNumber = params.pageNumber ?? 1;
    const pageSize = params.pageSize ?? DEFAULT_PAGE_SIZE;
    const range = params.earningsFilter
      ? toAffiliateEarningsRangePayload(params.earningsFilter)
      : { startDate: null, endDate: null };
    const { data } = await affiliateApi.referrals(
      {
        affiliateUserId:
          typeof params.affiliateUserId === 'number' && params.affiliateUserId > 0
            ? params.affiliateUserId
            : null,
        affiliateReferralCodeId:
          typeof params.affiliateReferralCodeId === 'number' &&
          params.affiliateReferralCodeId > 0
            ? params.affiliateReferralCodeId
            : null,
        qualificationStatusId: params.status
          ? affiliateQualificationStatusId(params.status)
          : null,
        searchKeyword: params.searchKeyword ? params.searchKeyword : null,
        startDate: range.startDate,
        endDate: range.endDate,
        // A page number, not a row offset — see the file header.
        startIndex: pageNumber,
        pageSize,
      },
      params.auth,
    );

    // `referrals` is nullable in the contract, so an empty page is valid.
    if (!isPagedEnvelope(data, 'referrals')) {
      throw new UnusableResponseError(AFFILIATE_ENDPOINTS.REFERRALS);
    }

    const totalCount = data.totalRecords ?? 0;
    const totalPages = pageSize > 0 ? Math.ceil(totalCount / pageSize) : 0;
    return {
      items: (Array.isArray(data.referrals) ? data.referrals : []).map(mapReferral),
      totalCount,
      eligibleCount: amount(data.eligibleCount),
      pendingCount: amount(data.pendingCount),
      rejectedCount: amount(data.rejectedCount),
      reversedCount: amount(data.reversedCount),
      eligibleEarnings: amount(data.eligibleEarnings),
      pendingEarnings: amount(data.pendingEarnings),
      pageNumber,
      pageSize,
      totalPages,
      hasNextPage: pageNumber < totalPages,
      hasPreviousPage: pageNumber > 1,
    };
  },

  /**
   * Records an operator's manual eligibility decision on one referral.
   *
   * `reviewComments` is mandatory even though the schema marks it nullable: the
   * stored procedure refuses an empty value. It is rejected here as well as in
   * the dialog so a decision can never be filed without the reason that
   * justifies moving money.
   */
  overrideReferralEligibility: async (payload: {
    affiliateReferralId: number;
    decision: AffiliateReferralOverrideDecision;
    reviewComments: string;
  }): Promise<AffiliateReferralOverride> => {
    const reviewComments = payload.reviewComments.trim();
    if (!reviewComments) {
      throw createAffiliateRequestError(null);
    }
    const { data } = await affiliateApi.overrideReferralEligibility({
      affiliateReferralId: payload.affiliateReferralId,
      statusId: affiliateQualificationStatusId(payload.decision),
      reviewComments,
    });
    if (!data || data.success === false || (data.statusCode ?? 0) >= 400) {
      throw createAffiliateRequestError(data?.message);
    }
    const referral = data.referral;
    if (!referral) {
      throw new UnusableResponseError(
        AFFILIATE_ENDPOINTS.OVERRIDE_REFERRAL_ELIGIBILITY,
      );
    }
    return {
      id: referral.affiliateReferralId,
      status: affiliateQualificationStatus(referral),
      statusLabel: text(referral.qualificationStatus),
      isEligible: referral.isEligible === true,
      reviewComments: text(referral.reviewComments),
      reviewedDate: text(referral.reviewedDate),
    };
  },

  getQualificationRule: async (): Promise<AffiliateQualificationRule> => {
    const { data } = await affiliateApi.qualificationRule();
    if (!data || data.success === false || (data.statusCode ?? 0) >= 400) {
      throw createAffiliateRequestError(data?.message);
    }
    if (!data.rule) {
      throw new UnusableResponseError(AFFILIATE_ENDPOINTS.QUALIFICATION_RULE);
    }
    return mapQualificationRule(data.rule);
  },

  /**
   * Replaces the active qualification thresholds.
   *
   * Not retroactive: referrals already graded keep the verdict they were given
   * under their own `appliedRuleId`, so this changes what happens next rather
   * than re-opening what happened.
   */
  updateQualificationRule: async (payload: {
    ruleName: string;
    ruleDescription: string;
    minTenureDays: number;
    minActiveLoginDays: number;
    minActivityCount: number;
    minActivityDistinctDays: number;
    requireEmailVerified: boolean;
    countPosts: boolean;
    countComments: boolean;
    countPostReactions: boolean;
    countPostShares: boolean;
    countCommentReactions: boolean;
    countGroupJoins: boolean;
  }): Promise<AffiliateQualificationRule> => {
    const { data } = await affiliateApi.updateQualificationRule({
      ruleName: payload.ruleName.trim() ? payload.ruleName.trim() : null,
      ruleDescription: payload.ruleDescription.trim()
        ? payload.ruleDescription.trim()
        : null,
      minTenureDays: payload.minTenureDays,
      minActiveLoginDays: payload.minActiveLoginDays,
      minActivityCount: payload.minActivityCount,
      minActivityDistinctDays: payload.minActivityDistinctDays,
      requireEmailVerified: payload.requireEmailVerified,
      countPosts: payload.countPosts,
      countComments: payload.countComments,
      countPostReactions: payload.countPostReactions,
      countPostShares: payload.countPostShares,
      countCommentReactions: payload.countCommentReactions,
      countGroupJoins: payload.countGroupJoins,
    });
    if (!data || data.success === false || (data.statusCode ?? 0) >= 400) {
      throw createAffiliateRequestError(data?.message);
    }
    if (!data.rule) {
      throw new UnusableResponseError(
        AFFILIATE_ENDPOINTS.UPDATE_QUALIFICATION_RULE,
      );
    }
    return mapQualificationRule(data.rule);
  },

  createReferralCode: async (
    payload: {
      affiliateUserId: number;
      referralCode: string;
      description?: string;
      commissionPerDownload: number;
      /** Operator creating the code for the affiliate; absent on portal calls. */
      actionBy?: number;
    },
    auth?: AffiliateAuth,
  ): Promise<void> => {
    const { data } = await affiliateApi.createReferralCode(
      {
        affiliateUserId: payload.affiliateUserId,
        referralCode: payload.referralCode,
        description: payload.description ?? null,
        commissionPerDownload: payload.commissionPerDownload,
        isActive: true,
        actionBy: payload.actionBy ?? null,
      },
      auth,
    );
    assertMutationAccepted(data, AFFILIATE_ENDPOINTS.CREATE_REFERRAL_CODE);
  },

  updateReferralCode: async (
    payload: {
      affiliateReferralCodeId: number;
      affiliateUserId: number;
      referralCode: string;
      description?: string;
      actionBy?: number;
    },
    auth?: AffiliateAuth,
  ): Promise<void> => {
    const { data } = await affiliateApi.updateReferralCode(
      {
        affiliateReferralCodeId: payload.affiliateReferralCodeId,
        affiliateUserId: payload.affiliateUserId,
        referralCode: payload.referralCode,
        description: payload.description ?? null,
        actionBy: payload.actionBy ?? null,
      },
      auth,
    );
    assertMutationAccepted(data, AFFILIATE_ENDPOINTS.UPDATE_REFERRAL_CODE);
  },

  deleteReferralCode: async (
    payload: {
      affiliateReferralCodeId: number;
      affiliateUserId: number;
      /** Operator deleting on the affiliate's behalf; absent on portal calls. */
      actionBy?: number;
    },
    auth?: AffiliateAuth,
  ): Promise<void> => {
    const { data } = await affiliateApi.deleteReferralCode(
      {
        affiliateReferralCodeId: payload.affiliateReferralCodeId,
        affiliateUserId: payload.affiliateUserId,
        actionBy: payload.actionBy ?? null,
      },
      auth,
    );
    assertMutationAccepted(data, AFFILIATE_ENDPOINTS.DELETE_REFERRAL_CODE);
  },

  /**
   * Submits a PayPal payout batch for one affiliate. PayPal settles
   * asynchronously, so the answer only says the batch was accepted; its final
   * state arrives on the API's webhook, which is why the returned status is
   * surfaced verbatim rather than translated into "paid".
   *
   * A refusal is recognised from `success` (which `apiClient` already rejects on)
   * and from the status *code* only. Deliberately not `rejectedByStatus`: that
   * treats any `status` string other than "success" as a failure, and this
   * envelope's `status` describes an asynchronous batch, so a perfectly accepted
   * `"PENDING"` would be reported to the operator as a failed payout — inviting
   * them to send the money a second time.
   */
  sendPayout: async (payload: {
    affiliateUserId: number;
    influencerEmail: string;
    amount: number;
    note?: string;
  }): Promise<AffiliatePayout> => {
    const { data } = await affiliateApi.sendPayout({
      affiliateUserId: payload.affiliateUserId,
      influencerEmail: payload.influencerEmail,
      amount: payload.amount,
      note: payload.note ? payload.note : null,
    });
    if (!data || data.success === false || (data.statusCode ?? 0) >= 400) {
      throw createAffiliateRequestError(data?.message);
    }
    return {
      batchId: text(data.batchId),
      batchStatus: text(data.batchStatus),
      message: text(data.message),
    };
  },
};

export default affiliateService;
